/**
 * Settings View
 * Manage profile, billing, branding assets, and credits
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Settings,
  User,
  Image as ImageIcon,
  Layers,
  Droplets,
  Upload,
  Trash2,
  Loader2,
  AlertCircle,
  Lock,
  CreditCard,
  ExternalLink,
  Mail,
  CheckCircle,
  Coins,
  Sparkles,
  Check,
  Gift,
  Building2,
  Download,
  Palette,
  Square,
  Grid3X3,
  CornerLeftUp,
  CornerRightUp,
  CornerLeftDown,
  CornerRightDown,
  Users,
  UserPlus,
  UserMinus,
  Clock,
  Send,
  ArrowDownToLine,
} from 'lucide-react';
import { BrandingAsset, BrandingAssetType } from '@/types';
import {
  fetchBrandingAssets,
  uploadBrandingAsset,
  uploadOverlayAsset,
  deleteBrandingAsset,
} from '@/services/brandingService';
import {
  createCustomerPortalLink,
  fetchUserProfile,
  updateUserProfile,
  updateDefaultAspectRatio,
  updateDefaultDownloadCompressed,
  updateDefaultBrandingDefaults,
  updateBrandingSettings,
  uploadBrandingLogo,
  getBrandingLogoUrl,
} from '@/services/paymentService';
import type { AspectRatio, LogoPosition, WatermarkPosition } from '@/types';
import { formatCredits } from '@/lib/utils';
import { overlaysForSelection, overlayGroupMembers } from '@/lib/overlayVariants';
import { supabase } from '@/lib/supabase';
import { uploadAvatar, deleteAvatar, getAvatarUrl } from '@/services/avatarService';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";

// ============================================================================
// Types
// ============================================================================

export type SettingsTab = 'profile' | 'branding' | 'affiliate' | 'team';

interface TeamMember {
  id: string;
  email: string;
  status: 'active' | 'pending';
  member_id: string | null;
  created_at: string;
  member_name?: string | null;
  member_credits?: number | null;
  avatar_path?: string | null;
}

interface SettingsViewProps {
  userId: string;
  credits?: number;
  initialTab?: SettingsTab;
}

// ============================================================================
// Main Component
// ============================================================================

export const SettingsView: React.FC<SettingsViewProps> = ({ userId, credits: initialCredits = 0, initialTab }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab ?? 'profile');
  // Re-navigating into settings with a different target tab (e.g. from the account menu)
  // switches the tab without clobbering the user's manual tab changes.
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);
  const [isLoading, setIsLoading] = useState(true);

  // Profile state
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);

  // Referral code state (retroactive entry)
  const [profileReferredBy, setProfileReferredBy] = useState<string | null>(null);
  const [profileCreatedAt, setProfileCreatedAt] = useState<string | null>(null);
  const [referralInput, setReferralInput] = useState('');
  const [isSavingReferral, setIsSavingReferral] = useState(false);
  const [referralSuccess, setReferralSuccess] = useState(false);

  // Avatar state
  const [avatarPath, setAvatarPath] = useState<string | null>(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Aspect ratio state
  const [defaultAspectRatio, setDefaultAspectRatio] = useState<AspectRatio | null>(null);
  const [isSavingAspectRatio, setIsSavingAspectRatio] = useState(false);
  const [aspectRatioSuccess, setAspectRatioSuccess] = useState(false);

  // Download setting state
  const [defaultDownloadCompressed, setDefaultDownloadCompressed] = useState(true);
  const [isSavingDownloadSetting, setIsSavingDownloadSetting] = useState(false);
  const [downloadSettingSuccess, setDownloadSettingSuccess] = useState(false);

  // Password state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Portal state
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  // Branding state
  const [logos, setLogos] = useState<BrandingAsset[]>([]);
  const [overlays, setOverlays] = useState<BrandingAsset[]>([]);
  const [watermarks, setWatermarks] = useState<BrandingAsset[]>([]);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingOverlay, setIsUploadingOverlay] = useState(false);
  const [isUploadingWatermark, setIsUploadingWatermark] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [isDraggingOverlay, setIsDraggingOverlay] = useState(false);
  const [isDraggingWatermark, setIsDraggingWatermark] = useState(false);

  // Credits state
  const [credits, setCredits] = useState(initialCredits);

  // Affiliate settings state
  const [affiliateIban, setAffiliateIban] = useState('');
  const [affiliateBic, setAffiliateBic] = useState('');
  const [affiliateAccountHolder, setAffiliateAccountHolder] = useState('');
  const [affiliateTaxId, setAffiliateTaxId] = useState('');
  const [isSavingAffiliate, setIsSavingAffiliate] = useState(false);
  const [affiliateSuccess, setAffiliateSuccess] = useState(false);
  const [affiliateError, setAffiliateError] = useState<string | null>(null);
  const [hasAffiliateSettings, setHasAffiliateSettings] = useState(false);

  // Upload Page Branding state
  const [companyName, setCompanyName] = useState('');
  const [brandingAccentColor, setBrandingAccentColor] = useState('#6366f1');
  const [brandingLogoPath, setBrandingLogoPath] = useState<string | null>(null);
  const [brandingLogoPreview, setBrandingLogoPreview] = useState<string | null>(null);
  const [isSavingBranding, setIsSavingBranding] = useState(false);
  const [isUploadingBrandingLogo, setIsUploadingBrandingLogo] = useState(false);
  const [brandingSuccess, setBrandingSuccess] = useState(false);
  const [brandingError, setBrandingError] = useState<string | null>(null);

  // Default branding/overlay settings state
  const [defaultLogoPosition, setDefaultLogoPosition] = useState<LogoPosition>('bottom-right');
  const [defaultWatermarkOpacity, setDefaultWatermarkOpacity] = useState(30);
  const [defaultWatermarkScale, setDefaultWatermarkScale] = useState(50);
  const [defaultWatermarkPosition, setDefaultWatermarkPosition] = useState<WatermarkPosition>('center');
  const [defaultLogoAssetId, setDefaultLogoAssetId] = useState<string | null>(null);
  const [defaultOverlayAssetId, setDefaultOverlayAssetId] = useState<string | null>(null);
  const [defaultWatermarkAssetId, setDefaultWatermarkAssetId] = useState<string | null>(null);
  const [isSavingBrandingDefaults, setIsSavingBrandingDefaults] = useState(false);
  const [brandingDefaultsSuccess, setBrandingDefaultsSuccess] = useState(false);

  // Team management state
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isTeamMember, setIsTeamMember] = useState(false); // True if current user is a member (not admin)
  const [teamAdminInfo, setTeamAdminInfo] = useState<{ admin_id: string; admin_email: string; admin_name: string } | null>(null);
  const [isLoadingTeam, setIsLoadingTeam] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  // Credit transfer state
  const [transferAmounts, setTransferAmounts] = useState<Record<string, string>>({});
  const [isTransferring, setIsTransferring] = useState<string | null>(null);

  // ============================================================================
  // Load Data
  // ============================================================================

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load profile
        const profile = await fetchUserProfile(userId);
        if (profile) {
          setEmail(user?.email || profile.email);
          setFullName(profile.fullName);
          setCredits(profile.credits);
          setDefaultAspectRatio(profile.defaultAspectRatio);
          setDefaultDownloadCompressed(profile.defaultDownloadCompressed);
          // Load default branding/overlay settings
          setDefaultLogoPosition(profile.defaultLogoPosition);
          setDefaultWatermarkOpacity(profile.defaultWatermarkOpacity);
          setDefaultWatermarkScale(Math.round(profile.defaultWatermarkScale * 100));
          setDefaultWatermarkPosition(profile.defaultWatermarkPosition);
          setDefaultLogoAssetId(profile.defaultLogoAssetId);
          setDefaultOverlayAssetId(profile.defaultOverlayAssetId);
          setDefaultWatermarkAssetId(profile.defaultWatermarkAssetId);
          // Load branding settings
          setCompanyName(profile.companyName || '');
          setBrandingAccentColor(profile.brandingAccentColor || '#6366f1');
          setBrandingLogoPath(profile.brandingLogoPath || null);
          if (profile.brandingLogoPath) {
            setBrandingLogoPreview(getBrandingLogoUrl(profile.brandingLogoPath));
          }
          // Load avatar
          setAvatarPath(profile.avatarPath);
          if (profile.avatarPath) {
            setAvatarPreviewUrl(getAvatarUrl(profile.avatarPath));
          }
        }

        // Load referral data for retroactive entry
        const { data: referralData } = await supabase
          .from('profiles')
          .select('referred_by, created_at')
          .eq('id', userId)
          .single();
        if (referralData) {
          setProfileReferredBy(referralData.referred_by || null);
          setProfileCreatedAt(referralData.created_at);
        }

        // Load branding assets
        const [logoData, overlayData, watermarkData] = await Promise.all([
          fetchBrandingAssets(userId, 'logo'),
          fetchBrandingAssets(userId, 'overlay'),
          fetchBrandingAssets(userId, 'watermark'),
        ]);
        setLogos(logoData);
        setOverlays(overlayData);
        setWatermarks(watermarkData);

        // Load affiliate settings
        const { data: affiliateData } = await supabase
          .from('affiliate_settings')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (affiliateData) {
          setAffiliateIban(affiliateData.iban || '');
          setAffiliateBic(affiliateData.bic || '');
          setAffiliateAccountHolder(affiliateData.account_holder || '');
          setAffiliateTaxId(affiliateData.tax_id || '');
          setHasAffiliateSettings(true);
        }

        // Load team data - check if user is a team member or admin
        setIsLoadingTeam(true);
        try {
          // Use get_admin_settings() which returns all admin data in one call (via SECURITY DEFINER)
          const { data: adminSettingsData, error: adminSettingsError } = await supabase
            .rpc('get_admin_settings');

          if (!adminSettingsError && adminSettingsData && adminSettingsData.length > 0) {
            // User is a team member - load admin's data
            setIsTeamMember(true);
            const adminSettings = adminSettingsData[0];
            
            // Set team admin info
            setTeamAdminInfo({
              admin_id: adminSettings.admin_id,
              admin_email: adminSettings.admin_email,
              admin_name: adminSettings.admin_name,
            });

            // Override aspect ratio with admin's default
            setDefaultAspectRatio(adminSettings.default_aspect_ratio as AspectRatio || null);
            
            // Load admin's default branding/overlay settings (read-only for members)
            setDefaultLogoPosition((adminSettings.default_logo_position as LogoPosition) || 'bottom-right');
            setDefaultWatermarkOpacity(adminSettings.default_watermark_opacity ?? 30);
            setDefaultWatermarkScale(adminSettings.default_watermark_scale ?? 50);
            setDefaultWatermarkPosition((adminSettings.default_watermark_position as WatermarkPosition) || 'center');
            setDefaultLogoAssetId(adminSettings.default_logo_asset_id || null);
            setDefaultOverlayAssetId(adminSettings.default_overlay_asset_id || null);
            setDefaultWatermarkAssetId(adminSettings.default_watermark_asset_id || null);

            // Load admin's branding settings (read-only for members)
            setCompanyName(adminSettings.company_name || '');
            setBrandingAccentColor(adminSettings.branding_accent_color || '#6366f1');
            setBrandingLogoPath(adminSettings.branding_logo_path || null);
            if (adminSettings.branding_logo_path) {
              setBrandingLogoPreview(getBrandingLogoUrl(adminSettings.branding_logo_path));
            }

            // Load admin's branding assets (RLS now allows reading admin's assets)
            const [adminLogos, adminOverlays, adminWatermarks] = await Promise.all([
              fetchBrandingAssets(adminSettings.admin_id, 'logo'),
              fetchBrandingAssets(adminSettings.admin_id, 'overlay'),
              fetchBrandingAssets(adminSettings.admin_id, 'watermark'),
            ]);
            // Set them as the active assets (for display)
            setLogos(adminLogos);
            setOverlays(adminOverlays);
            setWatermarks(adminWatermarks);
          } else {
            // User is an admin (or standalone user) - load their team members
            setIsTeamMember(false);
            const { data: membersData } = await supabase
              .rpc('get_team_members');
            
            if (membersData) {
              setTeamMembers(membersData);
            }
          }
        } catch (teamError) {
          console.error('Error loading team data:', teamError);
        } finally {
          setIsLoadingTeam(false);
        }
      } catch (error) {
        console.error('Error loading settings data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [userId, user]);

  // ============================================================================
  // Profile Handlers
  // ============================================================================

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    setProfileSuccess(false);
    
    const result = await updateUserProfile(userId, fullName);
    
    if (result.success) {
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    }
    
    setIsSavingProfile(false);
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Maximal 2 MB erlaubt.');
      return;
    }
    setIsUploadingAvatar(true);
    try {
      const path = await uploadAvatar(userId, file);
      setAvatarPath(path);
      setAvatarPreviewUrl(getAvatarUrl(path));
    } catch (error) {
      console.error('[Avatar] Upload failed:', error);
      alert('Avatar konnte nicht hochgeladen werden.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleAvatarDelete = async () => {
    if (!avatarPath) return;
    try {
      await deleteAvatar(userId, avatarPath);
      setAvatarPath(null);
      setAvatarPreviewUrl(null);
    } catch (error) {
      console.error('[Avatar] Delete failed:', error);
    }
  };

  const handleSaveAspectRatio = async (newRatio: AspectRatio | null) => {
    setIsSavingAspectRatio(true);
    setAspectRatioSuccess(false);
    setDefaultAspectRatio(newRatio);
    
    const result = await updateDefaultAspectRatio(userId, newRatio);
    
    if (result.success) {
      setAspectRatioSuccess(true);
      setTimeout(() => setAspectRatioSuccess(false), 3000);
    }
    
    setIsSavingAspectRatio(false);
  };

  const handleSaveDownloadSetting = async (compressed: boolean) => {
    setIsSavingDownloadSetting(true);
    setDownloadSettingSuccess(false);
    setDefaultDownloadCompressed(compressed);

    const result = await updateDefaultDownloadCompressed(userId, compressed);

    if (result.success) {
      setDownloadSettingSuccess(true);
      setTimeout(() => setDownloadSettingSuccess(false), 3000);
    }

    setIsSavingDownloadSetting(false);
  };

  const handleSaveBrandingDefaults = async () => {
    setIsSavingBrandingDefaults(true);
    setBrandingDefaultsSuccess(false);

    const result = await updateDefaultBrandingDefaults(userId, {
      logoPosition: defaultLogoPosition,
      watermarkOpacity: defaultWatermarkOpacity,
      watermarkScale: defaultWatermarkScale,
      watermarkPosition: defaultWatermarkPosition,
      logoAssetId: defaultLogoAssetId,
      overlayAssetId: defaultOverlayAssetId,
      watermarkAssetId: defaultWatermarkAssetId,
    });

    if (result.success) {
      setBrandingDefaultsSuccess(true);
      setTimeout(() => setBrandingDefaultsSuccess(false), 3000);
    }

    setIsSavingBrandingDefaults(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword.length < 6) {
      setPasswordError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setIsSavingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      
      if (error) {
        setPasswordError(error.message);
      } else {
        setPasswordSuccess(true);
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => setPasswordSuccess(false), 3000);
      }
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Fehler beim Ändern des Passworts');
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleOpenPortal = async () => {
    setIsLoadingPortal(true);
    setPortalError(null);

    const result = await createCustomerPortalLink();
    
    if (result.url) {
      window.location.href = result.url;
    } else {
      setPortalError(result.error || 'Fehler beim Öffnen des Portals');
      setIsLoadingPortal(false);
    }
  };

  // ============================================================================
  // Affiliate Handlers
  // ============================================================================

  const handleSaveAffiliateSettings = async () => {
    if (!affiliateIban || !affiliateAccountHolder) {
      setAffiliateError('IBAN und Kontoinhaber sind Pflichtfelder.');
      return;
    }

    setIsSavingAffiliate(true);
    setAffiliateError(null);
    setAffiliateSuccess(false);

    try {
      const affiliateData = {
        user_id: userId,
        iban: affiliateIban.replace(/\s/g, '').toUpperCase(),
        bic: affiliateBic.toUpperCase() || null,
        account_holder: affiliateAccountHolder,
        tax_id: affiliateTaxId || null,
        updated_at: new Date().toISOString(),
      };

      if (hasAffiliateSettings) {
        // Update existing
        const { error } = await supabase
          .from('affiliate_settings')
          .update(affiliateData)
          .eq('user_id', userId);

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('affiliate_settings')
          .insert({ ...affiliateData, created_at: new Date().toISOString() });

        if (error) throw error;
        setHasAffiliateSettings(true);
      }

      setAffiliateSuccess(true);
      setTimeout(() => setAffiliateSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving affiliate settings:', error);
      setAffiliateError('Fehler beim Speichern der Bankdaten.');
    } finally {
      setIsSavingAffiliate(false);
    }
  };

  // ============================================================================
  // Branding Handlers
  // ============================================================================

  const handleUpload = useCallback(
    async (files: FileList | File[], type: BrandingAssetType) => {
      const setUploading = type === 'logo' 
        ? setIsUploadingLogo 
        : type === 'overlay' 
          ? setIsUploadingOverlay 
          : setIsUploadingWatermark;
      const setAssets = type === 'logo' 
        ? setLogos 
        : type === 'overlay' 
          ? setOverlays 
          : setWatermarks;

      setUploadError(null);
      setUploading(true);

      try {
        for (const file of Array.from(files)) {
          if (!file.type.startsWith('image/')) {
            throw new Error('Nur Bilddateien sind erlaubt');
          }
          // Overlays: Seitenverhältnis automatisch erkennen (4:3/1:1) und taggen.
          const asset = type === 'overlay'
            ? await uploadOverlayAsset(userId, file)
            : await uploadBrandingAsset(userId, file, type);
          setAssets((prev) => [asset, ...prev]);
        }
      } catch (error) {
        console.error('Upload error:', error);
        setUploadError(error instanceof Error ? error.message : 'Upload fehlgeschlagen');
      } finally {
        setUploading(false);
      }
    },
    [userId]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: BrandingAssetType) => {
      if (e.target.files && e.target.files.length > 0) {
        handleUpload(e.target.files, type);
      }
      e.target.value = '';
    },
    [handleUpload]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent, type: BrandingAssetType) => {
      e.preventDefault();
      const setDragging = type === 'logo' 
        ? setIsDraggingLogo 
        : type === 'overlay' 
          ? setIsDraggingOverlay 
          : setIsDraggingWatermark;
      setDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleUpload(e.dataTransfer.files, type);
      }
    },
    [handleUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDelete = useCallback(async (asset: BrandingAsset) => {
    // Overlays: beide Formate (4:3 + 1:1) derselben Gruppe zusammen löschen.
    const members = asset.type === 'overlay' ? overlayGroupMembers(asset, overlays) : [asset];
    const multi = members.length > 1;
    if (!confirm(
      multi
        ? `"${asset.name}" wirklich löschen? Beide Formate (4:3 & 1:1) werden entfernt.`
        : `"${asset.name}" wirklich löschen?`
    )) return;

    const setAssets = asset.type === 'logo'
      ? setLogos
      : asset.type === 'overlay'
        ? setOverlays
        : setWatermarks;

    try {
      await Promise.all(members.map((m) => deleteBrandingAsset(m.id, m.storagePath)));
      const removed = new Set(members.map((m) => m.id));
      setAssets((prev) => prev.filter((a) => !removed.has(a.id)));
    } catch (error) {
      console.error('Delete error:', error);
      alert('Löschen fehlgeschlagen');
    }
  }, [overlays]);

  // ============================================================================
  // Team Handlers
  // ============================================================================

  const handleInviteTeamMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    setInviteSuccess(null);

    if (!inviteEmail || !inviteEmail.includes('@')) {
      setInviteError('Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }

    setIsInviting(true);

    try {
      const { data, error } = await supabase.functions.invoke('manage-team', {
        body: { action: 'invite', email: inviteEmail },
      });

      if (error) {
        setInviteError(error.message || 'Fehler beim Einladen');
        return;
      }

      if (!data.success) {
        setInviteError(data.error || 'Fehler beim Einladen');
        return;
      }

      // Success - add the new member to the list
      if (data.data?.member) {
        setTeamMembers((prev) => [data.data.member, ...prev]);
      }
      
      setInviteSuccess(data.message || 'Erfolgreich eingeladen');
      setInviteEmail('');
      setTimeout(() => setInviteSuccess(null), 5000);
    } catch (err) {
      console.error('Invite error:', err);
      setInviteError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveTeamMember = async (memberId: string) => {
    const member = teamMembers.find(m => m.id === memberId);
    const memberCredits = member?.member_credits ?? 0;
    const msg = memberCredits > 0
      ? `Möchtest du dieses Teammitglied entfernen? ${formatCredits(memberCredits)} Credits werden an dich zurücküberwiesen.`
      : 'Möchtest du dieses Teammitglied wirklich entfernen?';
    if (!confirm(msg)) {
      return;
    }

    setRemovingMemberId(memberId);

    try {
      const { data, error } = await supabase.functions.invoke('manage-team', {
        body: { action: 'remove', memberId },
      });

      if (error) {
        alert(error.message || 'Fehler beim Entfernen');
        return;
      }

      if (!data.success) {
        alert(data.error || 'Fehler beim Entfernen');
        return;
      }

      // Success - remove from list and reclaim credits
      setTeamMembers((prev) => prev.filter((m) => m.id !== memberId));
      if (data.reclaimedCredits > 0) {
        setCredits(prev => prev + data.reclaimedCredits);
      }
    } catch (err) {
      console.error('Remove error:', err);
      alert(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setRemovingMemberId(null);
    }
  };

  // Credit transfer handler
  const handleTransferCredits = async (toUserId: string, amount: number) => {
    if (amount <= 0) return;
    setIsTransferring(toUserId);
    try {
      const { data, error } = await supabase.rpc('transfer_credits', {
        p_to_user_id: toUserId,
        p_amount: amount,
      });
      if (error) {
        alert(error.message || 'Fehler bei der Überweisung');
        return;
      }
      const result = data?.[0];
      if (!result?.success) {
        alert(result?.error_message || 'Überweisung fehlgeschlagen');
        return;
      }
      // Update local state
      setCredits(result.sender_remaining);
      setTeamMembers((prev) =>
        prev.map((m) =>
          m.member_id === toUserId
            ? { ...m, member_credits: result.receiver_new }
            : m
        )
      );
      setTransferAmounts((prev) => ({ ...prev, [toUserId]: '' }));
    } catch (err) {
      console.error('Transfer error:', err);
      alert(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setIsTransferring(null);
    }
  };

  // Credit withdraw handler (admin reclaims credits from member)
  const handleWithdrawCredits = async (fromUserId: string, amount: number) => {
    if (amount <= 0) return;
    setIsTransferring(fromUserId);
    try {
      const { data, error } = await supabase.rpc('withdraw_credits', {
        p_from_user_id: fromUserId,
        p_amount: amount,
      });
      if (error) {
        alert(error.message || 'Fehler beim Abziehen');
        return;
      }
      const result = data?.[0];
      if (!result?.success) {
        alert(result?.error_message || 'Abziehen fehlgeschlagen');
        return;
      }
      // Update local state
      setCredits(result.admin_new);
      setTeamMembers((prev) =>
        prev.map((m) =>
          m.member_id === fromUserId
            ? { ...m, member_credits: result.member_remaining }
            : m
        )
      );
      setTransferAmounts((prev) => ({ ...prev, [fromUserId]: '' }));
    } catch (err) {
      console.error('Withdraw error:', err);
      alert(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setIsTransferring(null);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  // Build tabs - Team tab always shown (different content for admins vs members)
  const tabs = [
    { id: 'profile' as const, label: 'Profil & Konto', icon: User },
    { id: 'branding' as const, label: 'Branding', icon: ImageIcon },
    { id: 'team' as const, label: 'Team & Nutzer', icon: Users },
    ...(!isTeamMember ? [{ id: 'affiliate' as const, label: 'Partnerkonto', icon: Gift }] : []),
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
          <Settings className="w-8 h-8 text-primary" />
          Einstellungen
        </h1>
        <p className="text-muted-foreground mt-2">
          Verwalte dein Profil, Konto und Branding.
        </p>
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as SettingsTab)} className="space-y-6">
        <TabsList className={`grid w-full ${tabs.length >= 6 ? 'grid-cols-6' : tabs.length === 5 ? 'grid-cols-5' : 'grid-cols-4'} lg:w-auto lg:max-w-4xl`}>
          {tabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2">
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label.split(' ')[0]}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Profile & Billing Tab */}
        <TabsContent value="profile" className="space-y-6 max-w-4xl animate-fade-in">
          {/* Profile Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Mein Profil
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Avatar */}
              <div className="space-y-2">
                <Label>Profilbild</Label>
                <div className="flex items-center gap-4">
                  {avatarPreviewUrl ? (
                    <img src={avatarPreviewUrl} alt="Avatar" className="w-16 h-16 rounded-full object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center">
                      <User className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={isUploadingAvatar} asChild>
                      <label className="cursor-pointer">
                        {isUploadingAvatar ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                        Hochladen
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={handleAvatarUpload}
                        />
                      </label>
                    </Button>
                    {avatarPath && (
                      <Button variant="ghost" size="sm" onClick={handleAvatarDelete}>
                        <Trash2 className="w-4 h-4 mr-1" />
                        Entfernen
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Max. 2 MB. JPEG, PNG oder WebP.</p>
              </div>

              {/* Email (readonly) */}
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    readOnly
                    className="pl-9 bg-muted text-muted-foreground cursor-not-allowed"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Die E-Mail-Adresse kann nicht geändert werden.
                </p>
              </div>

              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Dein Name"
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile}
                >
                  {isSavingProfile && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
                {profileSuccess && (
                  <span className="text-emerald-600 text-sm flex items-center gap-1 animate-fade-in">
                    <CheckCircle className="w-4 h-4" /> Gespeichert
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Konto — Abo, Empfehlung, Sicherheit & Abrechnung */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Konto</h3>

          {/* Abo & Guthaben (Verwaltung auf der Preise-Seite) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                Abo &amp; Guthaben
              </CardTitle>
              <CardDescription>
                Verwalte dein Abo, buche das Grundriss-Paket dazu und lade bei Bedarf Credits nach.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-foreground">{formatCredits(credits)}</span>
                  <span className="text-sm text-muted-foreground">Credits</span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">1 Credit = 1 Bild-Generierung</p>
              </div>
              <Button onClick={() => window.location.assign('/app/credits')} className="gap-2">
                <ExternalLink className="h-4 w-4" /> Abo &amp; Guthaben verwalten
              </Button>
            </CardContent>
          </Card>

          {/* Referral Code Section */}
          {(() => {
            const canEnterReferral = !profileReferredBy && profileCreatedAt &&
              (Date.now() - new Date(profileCreatedAt).getTime()) < 30 * 24 * 60 * 60 * 1000;
            const daysRemaining = profileCreatedAt
              ? Math.max(0, 30 - Math.floor((Date.now() - new Date(profileCreatedAt).getTime()) / (24 * 60 * 60 * 1000)))
              : 0;

            if (profileReferredBy) {
              return (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Gift className="w-5 h-5 text-primary" />
                      Empfehlungscode
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <Input
                        value={profileReferredBy}
                        readOnly
                        className="font-mono uppercase max-w-[200px]"
                      />
                      <span className="text-sm text-muted-foreground">Gespeichert</span>
                    </div>
                  </CardContent>
                </Card>
              );
            }

            if (!canEnterReferral) return null;

            return (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-primary" />
                    Empfehlungscode
                  </CardTitle>
                  <CardDescription>
                    Wurdest du von einem Kollegen empfohlen? Gib hier den Code ein.
                    {daysRemaining > 0 && (
                      <span className="block mt-1 text-xs">
                        Noch {daysRemaining} {daysRemaining === 1 ? 'Tag' : 'Tage'} zum Eintragen.
                      </span>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <Input
                      value={referralInput}
                      onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                      placeholder="z.B. ABC12"
                      maxLength={5}
                      className="font-mono uppercase max-w-[200px]"
                    />
                    <Button
                      disabled={!referralInput.trim() || isSavingReferral}
                      onClick={async () => {
                        setIsSavingReferral(true);
                        setReferralSuccess(false);
                        try {
                          await supabase
                            .from('profiles')
                            .update({ referred_by: referralInput.trim().toUpperCase() })
                            .eq('id', userId);
                          setProfileReferredBy(referralInput.trim().toUpperCase());
                          setReferralSuccess(true);
                          setTimeout(() => setReferralSuccess(false), 3000);
                        } catch (err) {
                          console.error('[Settings] Error saving referral code:', err);
                        } finally {
                          setIsSavingReferral(false);
                        }
                      }}
                    >
                      {isSavingReferral && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Speichern
                    </Button>
                    {referralSuccess && (
                      <span className="text-emerald-600 text-sm flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" /> Gespeichert
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Sicherheit & Abrechnung nebeneinander */}
          <div className="grid items-start gap-5 sm:grid-cols-2">
          {/* Security Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                Sicherheit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Neues Passwort</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      minLength={6}
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Mindestens 6 Zeichen</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Passwort bestätigen</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      minLength={6}
                      className="pl-9"
                    />
                  </div>
                </div>

                {passwordError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Fehler</AlertTitle>
                    <AlertDescription>{passwordError}</AlertDescription>
                  </Alert>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={isSavingPassword || !newPassword || !confirmPassword}
                  >
                    {isSavingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Passwort ändern
                  </Button>
                  {passwordSuccess && (
                    <span className="text-emerald-600 text-sm flex items-center gap-1 animate-fade-in">
                      <CheckCircle className="w-4 h-4" /> Passwort geändert
                    </span>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Billing Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                Abrechnung
              </CardTitle>
              <CardDescription>
                Verwalte deine Rechnungsdaten, Zahlungsmethoden und lade vergangene Rechnungen herunter.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {portalError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Fehler</AlertTitle>
                  <AlertDescription>{portalError}</AlertDescription>
                </Alert>
              )}

              <Button
                variant="outline"
                onClick={handleOpenPortal}
                disabled={isLoadingPortal}
                className="w-full sm:w-auto"
              >
                {isLoadingPortal ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="mr-2 h-4 w-4" />
                )}
                Rechnungsdaten & Rechnungen verwalten
              </Button>
            </CardContent>
          </Card>
            </div>
          </div>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6 max-w-4xl animate-fade-in">
          <Accordion type="multiple" className="rounded-xl border border-border bg-card px-4">
          <AccordionItem value="bildformat">
            <AccordionTrigger className="text-sm font-semibold">Bildformat</AccordionTrigger>
            <AccordionContent className="space-y-6 pb-4">
          {/* Standard-Formate — Bild- & Download-Format nebeneinander */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Standard-Formate</h3>
            <div className="grid items-start gap-5 sm:grid-cols-2">
          {/* Aspect Ratio Section */}
          <Card className={isTeamMember ? 'opacity-80' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-primary" />
                Bildformat
                {isTeamMember && (
                  <Badge variant="secondary" className="ml-2 text-xs">Team-Standard</Badge>
                )}
              </CardTitle>
              <CardDescription>
                {isTeamMember 
                  ? 'Das Standard-Format wird von deinem Team-Administrator festgelegt.'
                  : 'Wähle das Standard-Format für generierte Bilder. Du kannst dies für jedes Projekt individuell ändern.'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {/* 4:3 Option */}
                <label 
                  className={`relative flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                    isTeamMember ? 'cursor-not-allowed' : 'cursor-pointer'
                  } ${
                    defaultAspectRatio === '4:3' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-muted hover:border-muted-foreground/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="aspectRatio"
                    checked={defaultAspectRatio === '4:3'}
                    onChange={() => !isTeamMember && handleSaveAspectRatio('4:3')}
                    disabled={isTeamMember}
                    className="sr-only"
                  />
                  <div className="w-12 h-9 bg-muted rounded border-2 border-muted-foreground/20 flex items-center justify-center">
                    <span className="text-xs font-mono text-muted-foreground">4:3</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">4:3 Querformat</p>
                    <p className="text-sm text-muted-foreground">Immobilienstandard, ideal für Exposés und Portale</p>
                  </div>
                  {defaultAspectRatio === '4:3' && (
                    <Check className="w-5 h-5 text-primary" />
                  )}
                </label>

                {/* 1:1 Option */}
                <label 
                  className={`relative flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                    isTeamMember ? 'cursor-not-allowed' : 'cursor-pointer'
                  } ${
                    defaultAspectRatio === '1:1' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-muted hover:border-muted-foreground/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="aspectRatio"
                    checked={defaultAspectRatio === '1:1'}
                    onChange={() => !isTeamMember && handleSaveAspectRatio('1:1')}
                    disabled={isTeamMember}
                    className="sr-only"
                  />
                  <div className="w-10 h-10 bg-muted rounded border-2 border-muted-foreground/20 flex items-center justify-center">
                    <span className="text-xs font-mono text-muted-foreground">1:1</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">1:1 Quadratisch</p>
                    <p className="text-sm text-muted-foreground">Perfekt für Social Media und Instagram</p>
                  </div>
                  {defaultAspectRatio === '1:1' && (
                    <Check className="w-5 h-5 text-primary" />
                  )}
                </label>

                {/* No Default Option - Hidden for team members */}
                {!isTeamMember && (
                <label 
                  className={`relative flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    defaultAspectRatio === null 
                      ? 'border-primary bg-primary/5' 
                      : 'border-muted hover:border-muted-foreground/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="aspectRatio"
                    checked={defaultAspectRatio === null}
                    onChange={() => handleSaveAspectRatio(null)}
                    className="sr-only"
                  />
                  <div className="w-10 h-10 bg-muted rounded border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                    <span className="text-lg text-muted-foreground">?</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">Kein Standard</p>
                    <p className="text-sm text-muted-foreground">Bei jedem neuen Projekt fragen</p>
                  </div>
                  {defaultAspectRatio === null && (
                    <Check className="w-5 h-5 text-primary" />
                  )}
                </label>
                )}
              </div>

              {!isTeamMember && (isSavingAspectRatio || aspectRatioSuccess) && (
                <div className="flex items-center gap-2 pt-2">
                  {isSavingAspectRatio && (
                    <span className="text-muted-foreground text-sm flex items-center gap-1">
                      <Loader2 className="w-4 h-4 animate-spin" /> Wird gespeichert...
                    </span>
                  )}
                  {aspectRatioSuccess && (
                    <span className="text-emerald-600 text-sm flex items-center gap-1 animate-fade-in">
                      <CheckCircle className="w-4 h-4" /> Gespeichert
                    </span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Download Format Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-primary" />
                Download-Format
              </CardTitle>
              <CardDescription>
                Wähle das Standard-Format für Einzel-Downloads. Bei ZIP-Exports kannst du immer zwischen beiden Optionen wählen.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {/* Compressed Option */}
                <label
                  className={`relative flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    defaultDownloadCompressed
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-muted-foreground/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="downloadFormat"
                    checked={defaultDownloadCompressed}
                    onChange={() => handleSaveDownloadSetting(true)}
                    className="sr-only"
                  />
                  <div className="w-12 h-9 bg-muted rounded border-2 border-muted-foreground/20 flex items-center justify-center">
                    <span className="text-[10px] font-mono text-muted-foreground">JPG</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">Komprimiert (Portalfreundlich)</p>
                    <p className="text-sm text-muted-foreground">2400px Breite, JPEG, unter 5 MB — ideal für ImmoScout24 & Co.</p>
                  </div>
                  {defaultDownloadCompressed && (
                    <Check className="w-5 h-5 text-primary" />
                  )}
                </label>

                {/* Original Option */}
                <label
                  className={`relative flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    !defaultDownloadCompressed
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-muted-foreground/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="downloadFormat"
                    checked={!defaultDownloadCompressed}
                    onChange={() => handleSaveDownloadSetting(false)}
                    className="sr-only"
                  />
                  <div className="w-12 h-9 bg-muted rounded border-2 border-muted-foreground/20 flex items-center justify-center">
                    <span className="text-[10px] font-mono text-muted-foreground">PNG</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">Originalauflösung</p>
                    <p className="text-sm text-muted-foreground">Volle Auflösung, PNG — maximale Qualität</p>
                  </div>
                  {!defaultDownloadCompressed && (
                    <Check className="w-5 h-5 text-primary" />
                  )}
                </label>
              </div>

              {(isSavingDownloadSetting || downloadSettingSuccess) && (
                <div className="flex items-center gap-2 pt-2">
                  {isSavingDownloadSetting && (
                    <span className="text-muted-foreground text-sm flex items-center gap-1">
                      <Loader2 className="w-4 h-4 animate-spin" /> Wird gespeichert...
                    </span>
                  )}
                  {downloadSettingSuccess && (
                    <span className="text-emerald-600 text-sm flex items-center gap-1 animate-fade-in">
                      <CheckCircle className="w-4 h-4" /> Gespeichert
                    </span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
            </div>
          </div>

            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="mieterlinks">
            <AccordionTrigger className="text-sm font-semibold">Mieterlinks</AccordionTrigger>
            <AccordionContent className="space-y-6 pb-4">
          {/* Upload Page Branding Section */}
          <Card className={isTeamMember ? 'opacity-80' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Upload-Seiten Branding
                {isTeamMember && (
                  <Badge variant="secondary" className="ml-2 text-xs">Team-Branding</Badge>
                )}
              </CardTitle>
              <CardDescription>
                {isTeamMember 
                  ? 'Das Branding wird von deinem Team-Administrator festgelegt.'
                  : 'Passe die Mieter-Upload-Seite mit deinem Firmenlogo und deinen Farben an.'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Company Name */}
              <div className="space-y-2">
                <Label htmlFor="companyName">Firmenname</Label>
                <Input
                  id="companyName"
                  type="text"
                  value={companyName}
                  onChange={(e) => !isTeamMember && setCompanyName(e.target.value)}
                  placeholder="z.B. Mustermann Immobilien"
                  disabled={isTeamMember}
                  className={isTeamMember ? 'cursor-not-allowed' : ''}
                />
                <p className="text-xs text-muted-foreground">
                  Wird als Fallback angezeigt, wenn kein Logo hochgeladen ist.
                </p>
              </div>

              {/* Logo Upload */}
              <div className="space-y-2">
                <Label>Firmenlogo</Label>
                <div className="flex items-start gap-4">
                  {/* Logo Preview */}
                  <div className="w-32 h-20 rounded-lg border-2 border-dashed border-muted flex items-center justify-center bg-muted/30 overflow-hidden">
                    {brandingLogoPreview ? (
                      <img 
                        src={brandingLogoPreview} 
                        alt="Logo" 
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
                    )}
                  </div>
                  
                  <div className="flex-1 space-y-2">
                    {!isTeamMember && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        disabled={isUploadingBrandingLogo}
                      >
                        <label className="cursor-pointer">
                          {isUploadingBrandingLogo ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Logo hochladen
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              
                              setIsUploadingBrandingLogo(true);
                              setBrandingError(null);
                              
                              try {
                                const result = await uploadBrandingLogo(userId, file);
                                if (result.success && result.path) {
                                  setBrandingLogoPath(result.path);
                                  setBrandingLogoPreview(getBrandingLogoUrl(result.path));
                                  // Auto-save
                                  await updateBrandingSettings(userId, { brandingLogoPath: result.path });
                                  setBrandingSuccess(true);
                                  setTimeout(() => setBrandingSuccess(false), 3000);
                                } else {
                                  setBrandingError(result.error || 'Upload fehlgeschlagen');
                                }
                              } catch {
                                setBrandingError('Upload fehlgeschlagen');
                              } finally {
                                setIsUploadingBrandingLogo(false);
                                e.target.value = '';
                              }
                            }}
                          />
                        </label>
                      </Button>
                      
                      {brandingLogoPath && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            setBrandingLogoPath(null);
                            setBrandingLogoPreview(null);
                            await updateBrandingSettings(userId, { brandingLogoPath: null });
                            setBrandingSuccess(true);
                            setTimeout(() => setBrandingSuccess(false), 3000);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Entfernen
                        </Button>
                      )}
                    </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {isTeamMember ? 'Vom Team-Administrator festgelegt.' : 'PNG, JPG, SVG oder WebP. Empfohlen: 200x60px transparent.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Accent Color */}
              <div className="space-y-2">
                <Label htmlFor="accentColor">Akzentfarbe</Label>
                <div className="relative flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg border-2 border-border ${isTeamMember ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    style={{ backgroundColor: brandingAccentColor }}
                    onClick={() => !isTeamMember && document.getElementById('colorPicker')?.click()}
                  />
                  <input
                    id="colorPicker"
                    type="color"
                    value={brandingAccentColor}
                    disabled={isTeamMember}
                    onChange={(e) => {
                      if (isTeamMember) return;
                      const color = e.target.value;
                      // Validate: not too light (for readability)
                      const hex = color.replace('#', '');
                      const r = parseInt(hex.substr(0, 2), 16);
                      const g = parseInt(hex.substr(2, 2), 16);
                      const b = parseInt(hex.substr(4, 2), 16);
                      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
                      
                      if (brightness > 200) {
                        setBrandingError('Diese Farbe ist zu hell für gute Lesbarkeit. Bitte wähle eine dunklere Farbe.');
                        return;
                      }
                      setBrandingError(null);
                      setBrandingAccentColor(color);
                    }}
                    className="sr-only"
                  />
                  <Input
                    type="text"
                    value={brandingAccentColor}
                    disabled={isTeamMember}
                    onChange={(e) => {
                      if (isTeamMember) return;
                      const color = e.target.value;
                      if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
                        const hex = color.replace('#', '');
                        const r = parseInt(hex.substr(0, 2), 16);
                        const g = parseInt(hex.substr(2, 2), 16);
                        const b = parseInt(hex.substr(4, 2), 16);
                        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
                        
                        if (brightness > 200) {
                          setBrandingError('Diese Farbe ist zu hell für gute Lesbarkeit.');
                          return;
                        }
                        setBrandingError(null);
                        setBrandingAccentColor(color);
                      }
                    }}
                    placeholder="#6366f1"
                    className={`w-28 font-mono ${isTeamMember ? 'cursor-not-allowed' : ''}`}
                    maxLength={7}
                  />
                  
                  {/* Color Presets - hidden for team members */}
                  {!isTeamMember && (
                  <div className="flex gap-1">
                    {['#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316', '#22c55e', '#0ea5e9', '#6b7280'].map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${brandingAccentColor === color ? 'border-foreground scale-110' : 'border-transparent'}`}
                        style={{ backgroundColor: color }}
                        onClick={() => {
                          setBrandingAccentColor(color);
                          setBrandingError(null);
                        }}
                      />
                    ))}
                  </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {isTeamMember ? 'Vom Team-Administrator festgelegt.' : 'Wird für Buttons und Akzente auf der Upload-Seite verwendet.'}
                </p>
              </div>

              {/* Preview */}
              <div className="space-y-2">
                <Label>Vorschau</Label>
                <div className="p-4 rounded-lg border bg-background">
                  <div className="flex items-center gap-3">
                    {brandingLogoPreview ? (
                      <img 
                        src={brandingLogoPreview} 
                        alt="Logo" 
                        className="h-8 object-contain"
                      />
                    ) : (
                      <span 
                        className="text-xl font-bold"
                        style={{ color: brandingAccentColor }}
                      >
                        {companyName || 'Dein Firmenname'}
                      </span>
                    )}
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                      style={{ backgroundColor: brandingAccentColor }}
                    >
                      Fotos auswählen
                    </button>
                  </div>
                </div>
              </div>

              {brandingError && !isTeamMember && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{brandingError}</AlertDescription>
                </Alert>
              )}

              {/* Save button - hidden for team members */}
              {!isTeamMember && (
              <div className="flex items-center gap-3 pt-2">
                <Button
                  onClick={async () => {
                    setIsSavingBranding(true);
                    setBrandingSuccess(false);
                    setBrandingError(null);
                    
                    const result = await updateBrandingSettings(userId, {
                      companyName,
                      brandingAccentColor,
                    });
                    
                    if (result.success) {
                      setBrandingSuccess(true);
                      setTimeout(() => setBrandingSuccess(false), 3000);
                    } else {
                      setBrandingError(result.error || 'Fehler beim Speichern');
                    }
                    
                    setIsSavingBranding(false);
                  }}
                  disabled={isSavingBranding}
                >
                  {isSavingBranding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
                {brandingSuccess && (
                  <span className="text-emerald-600 text-sm flex items-center gap-1 animate-fade-in">
                    <CheckCircle className="w-4 h-4" /> Gespeichert
                  </span>
                )}
              </div>
              )}
            </CardContent>
          </Card>

            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="overlays" className="border-b-0">
            <AccordionTrigger className="text-sm font-semibold">Overlays</AccordionTrigger>
            <AccordionContent className="space-y-6 pb-4">
            {/* Overlay Builder — Einstieg (Sidebar-Eintrag wurde entfernt, Meeting 2026-07-07) */}
            <Card>
              <CardContent className="p-4 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <p className="font-semibold text-foreground">Overlay Builder</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Erstelle und verwalte deine Branding-Overlays (Masken) — einmal gebaut, überall im Export wählbar.
                  </p>
                </div>
                <Button onClick={() => window.location.assign('/app/overlay-builder')}>
                  Overlay Builder öffnen
                </Button>
              </CardContent>
            </Card>

            {/* Team Member Info Banner */}
            {isTeamMember && teamAdminInfo && (
              <Alert className="bg-blue-50 border-blue-200">
                <Users className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-800">Team-Branding</AlertTitle>
                <AlertDescription className="text-blue-700">
                  Du siehst die Branding-Assets von <strong>{teamAdminInfo.admin_name || teamAdminInfo.admin_email}</strong>. 
                  Diese können beim Staging verwendet, aber nicht bearbeitet werden.
                </AlertDescription>
              </Alert>
            )}

            {/* Error Message */}
            {uploadError && !isTeamMember && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Upload Fehler</AlertTitle>
                <AlertDescription>{uploadError}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Logos Section */}
              <Card className={`h-full ${isTeamMember ? 'opacity-80' : ''}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-base">
                    <span className="flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-primary" />
                      {isTeamMember ? 'Team-Logos' : 'Meine Logos'}
                    </span>
                    <Badge variant="secondary" className="text-xs font-normal">
                      {logos.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Upload dropzone - hidden for team members */}
                  {!isTeamMember && (
                  <label
                    className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                      isDraggingLogo
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-primary/50 hover:bg-muted/50'
                    }`}
                    onDragEnter={() => setIsDraggingLogo(true)}
                    onDragLeave={() => setIsDraggingLogo(false)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'logo')}
                  >
                    {isUploadingLogo ? (
                      <div className="flex flex-col items-center py-2">
                        <Loader2 className="w-6 h-6 text-primary animate-spin mb-2" />
                        <span className="text-sm text-muted-foreground">Wird hochgeladen...</span>
                      </div>
                    ) : (
                      <div className="py-2">
                        <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                        <span className="text-sm text-muted-foreground block">
                          Hier ablegen oder <span className="text-primary font-medium">klicken</span>
                        </span>
                        <span className="text-xs text-muted-foreground mt-1 block">PNG, JPG bis 5MB</span>
                      </div>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/png,image/jpeg"
                      multiple
                      onChange={(e) => handleFileChange(e, 'logo')}
                      disabled={isUploadingLogo}
                    />
                  </label>
                  )}

                  {logos.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground/50">
                      <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Noch keine Logos</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {logos.map((asset) => (
                        <AssetCard key={asset.id} asset={asset} onDelete={handleDelete} readOnly={isTeamMember} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Overlays Section */}
              <Card className={`h-full ${isTeamMember ? 'opacity-80' : ''}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-base">
                    <span className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-primary" />
                      {isTeamMember ? 'Team-Overlays' : 'Meine Overlays'}
                    </span>
                    <Badge variant="secondary" className="text-xs font-normal">
                      {overlaysForSelection(overlays).length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Upload dropzone - hidden for team members */}
                  {!isTeamMember && (
                  <label
                    className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                      isDraggingOverlay
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-primary/50 hover:bg-muted/50'
                    }`}
                    onDragEnter={() => setIsDraggingOverlay(true)}
                    onDragLeave={() => setIsDraggingOverlay(false)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'overlay')}
                  >
                    {isUploadingOverlay ? (
                      <div className="flex flex-col items-center py-2">
                        <Loader2 className="w-6 h-6 text-primary animate-spin mb-2" />
                        <span className="text-sm text-muted-foreground">Wird hochgeladen...</span>
                      </div>
                    ) : (
                      <div className="py-2">
                        <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                        <span className="text-sm text-muted-foreground block">
                          Hier ablegen oder <span className="text-primary font-medium">klicken</span>
                        </span>
                        <span className="text-xs text-muted-foreground mt-1 block">PNG (1:1) Transp.</span>
                      </div>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/png"
                      multiple
                      onChange={(e) => handleFileChange(e, 'overlay')}
                      disabled={isUploadingOverlay}
                    />
                  </label>
                  )}

                  {overlaysForSelection(overlays).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground/50">
                      <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Noch keine Overlays</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {overlaysForSelection(overlays).map((asset) => (
                        <AssetCard key={asset.id} asset={asset} onDelete={handleDelete} isOverlay readOnly={isTeamMember} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Watermarks Section */}
              <Card className={`h-full ${isTeamMember ? 'opacity-80' : ''}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-base">
                    <span className="flex items-center gap-2">
                      <Droplets className="w-4 h-4 text-primary" />
                      {isTeamMember ? 'Team-Wasserzeichen' : 'Wasserzeichen'}
                    </span>
                    <Badge variant="secondary" className="text-xs font-normal">
                      {watermarks.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Upload dropzone - hidden for team members */}
                  {!isTeamMember && (
                  <label
                    className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                      isDraggingWatermark
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-primary/50 hover:bg-muted/50'
                    }`}
                    onDragEnter={() => setIsDraggingWatermark(true)}
                    onDragLeave={() => setIsDraggingWatermark(false)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'watermark')}
                  >
                    {isUploadingWatermark ? (
                      <div className="flex flex-col items-center py-2">
                        <Loader2 className="w-6 h-6 text-primary animate-spin mb-2" />
                        <span className="text-sm text-muted-foreground">Wird hochgeladen...</span>
                      </div>
                    ) : (
                      <div className="py-2">
                        <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                        <span className="text-sm text-muted-foreground block">
                          Hier ablegen oder <span className="text-primary font-medium">klicken</span>
                        </span>
                        <span className="text-xs text-muted-foreground mt-1 block">SW PNG</span>
                      </div>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/png,image/jpeg"
                      multiple
                      onChange={(e) => handleFileChange(e, 'watermark')}
                      disabled={isUploadingWatermark}
                    />
                  </label>
                  )}

                  {watermarks.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground/50">
                      <Droplets className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Noch keine Wasserzeichen</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {watermarks.map((asset) => (
                        <AssetCard key={asset.id} asset={asset} onDelete={handleDelete} isWatermark readOnly={isTeamMember} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Standard-Branding Defaults Card */}
            <Card className={isTeamMember ? 'opacity-80' : ''}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-primary" />
                  Overlay Einstellungen
                  {isTeamMember && <Badge variant="secondary" className="text-xs font-normal">Team-Standard</Badge>}
                </CardTitle>
                <CardDescription>
                  Lege Standardwerte fest, die beim Öffnen des Overlay-Tools und der Branding-Ansicht automatisch vorausgefüllt werden.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Asset Selection: 3 columns side-by-side */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Logo Column */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Logo</Label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => !isTeamMember && setDefaultLogoAssetId(null)}
                        disabled={isTeamMember}
                        className={`h-20 w-20 rounded-lg border-2 flex items-center justify-center text-xs text-muted-foreground transition-all shrink-0 ${
                          defaultLogoAssetId === null
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-muted hover:border-muted-foreground/30'
                        }`}
                      >
                        Keins
                      </button>
                      {logos.map((asset) => (
                        <button
                          key={asset.id}
                          onClick={() => !isTeamMember && setDefaultLogoAssetId(asset.id)}
                          disabled={isTeamMember}
                          className={`h-20 w-20 rounded-lg border-2 overflow-hidden transition-all shrink-0 ${
                            defaultLogoAssetId === asset.id
                              ? 'border-primary ring-2 ring-primary/20'
                              : 'border-muted hover:border-muted-foreground/30'
                          }`}
                        >
                          <img src={asset.publicUrl} alt={asset.name || 'Logo'} className="w-full h-full object-contain p-1" />
                        </button>
                      ))}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Position</Label>
                      <div className="grid grid-cols-4 gap-1.5">
                        {([
                          { position: 'top-left' as LogoPosition, label: 'OL' },
                          { position: 'top-right' as LogoPosition, label: 'OR' },
                          { position: 'bottom-left' as LogoPosition, label: 'UL' },
                          { position: 'bottom-right' as LogoPosition, label: 'UR' },
                        ]).map(({ position, label }) => (
                          <Button
                            key={position}
                            variant={defaultLogoPosition === position ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setDefaultLogoPosition(position)}
                            disabled={isTeamMember}
                            className="text-xs h-7"
                          >
                            {label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Overlay Column */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Overlay</Label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => !isTeamMember && setDefaultOverlayAssetId(null)}
                        disabled={isTeamMember}
                        className={`h-20 w-20 rounded-lg border-2 flex items-center justify-center text-xs text-muted-foreground transition-all shrink-0 ${
                          defaultOverlayAssetId === null
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-muted hover:border-muted-foreground/30'
                        }`}
                      >
                        Keins
                      </button>
                      {overlaysForSelection(overlays).map((asset) => (
                        <button
                          key={asset.id}
                          onClick={() => !isTeamMember && setDefaultOverlayAssetId(asset.id)}
                          disabled={isTeamMember}
                          className={`h-20 w-20 rounded-lg border-2 overflow-hidden transition-all shrink-0 ${
                            defaultOverlayAssetId === asset.id
                              ? 'border-primary ring-2 ring-primary/20'
                              : 'border-muted hover:border-muted-foreground/30'
                          }`}
                        >
                          <img src={asset.publicUrl} alt={asset.name || 'Overlay'} className="w-full h-full object-contain p-1" />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Watermark Column */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Wasserzeichen</Label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => !isTeamMember && setDefaultWatermarkAssetId(null)}
                        disabled={isTeamMember}
                        className={`h-20 w-20 rounded-lg border-2 flex items-center justify-center text-xs text-muted-foreground transition-all shrink-0 ${
                          defaultWatermarkAssetId === null
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-muted hover:border-muted-foreground/30'
                        }`}
                      >
                        Keins
                      </button>
                      {watermarks.map((asset) => (
                        <button
                          key={asset.id}
                          onClick={() => !isTeamMember && setDefaultWatermarkAssetId(asset.id)}
                          disabled={isTeamMember}
                          className={`h-20 w-20 rounded-lg border-2 overflow-hidden transition-all shrink-0 ${
                            defaultWatermarkAssetId === asset.id
                              ? 'border-primary ring-2 ring-primary/20'
                              : 'border-muted hover:border-muted-foreground/30'
                          }`}
                        >
                          <img src={asset.publicUrl} alt={asset.name || 'Wasserzeichen'} className="w-full h-full object-contain p-1 bg-muted" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Watermark Settings: Sliders side-by-side */}
                <div className="border-t pt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Wasserzeichen-Deckkraft: {defaultWatermarkOpacity}%</Label>
                      <Slider
                        value={[defaultWatermarkOpacity]}
                        onValueChange={([value]) => setDefaultWatermarkOpacity(value)}
                        min={5}
                        max={80}
                        step={1}
                        disabled={isTeamMember}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Wasserzeichen-Größe: {defaultWatermarkScale}%</Label>
                      <Slider
                        value={[defaultWatermarkScale]}
                        onValueChange={([value]) => setDefaultWatermarkScale(value)}
                        min={10}
                        max={100}
                        step={5}
                        disabled={isTeamMember}
                      />
                    </div>
                  </div>

                  {/* Watermark Position: single row */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Wasserzeichen-Position</Label>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5">
                      {([
                        { position: 'center' as WatermarkPosition, icon: <Square className="w-3.5 h-3.5" />, label: 'Mitte' },
                        { position: 'tile' as WatermarkPosition, icon: <Grid3X3 className="w-3.5 h-3.5" />, label: 'Kachel' },
                        { position: 'tl' as WatermarkPosition, icon: <CornerLeftUp className="w-3.5 h-3.5" />, label: 'OL' },
                        { position: 'tr' as WatermarkPosition, icon: <CornerRightUp className="w-3.5 h-3.5" />, label: 'OR' },
                        { position: 'bl' as WatermarkPosition, icon: <CornerLeftDown className="w-3.5 h-3.5" />, label: 'UL' },
                        { position: 'br' as WatermarkPosition, icon: <CornerRightDown className="w-3.5 h-3.5" />, label: 'UR' },
                      ]).map(({ position, icon, label }) => (
                        <Button
                          key={position}
                          variant={defaultWatermarkPosition === position ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setDefaultWatermarkPosition(position)}
                          disabled={isTeamMember}
                          className="text-xs flex items-center gap-1 h-7"
                        >
                          {icon}
                          {label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                {!isTeamMember && (
                  <div className="flex items-center gap-3 pt-2">
                    <Button
                      onClick={handleSaveBrandingDefaults}
                      disabled={isSavingBrandingDefaults}
                      size="sm"
                    >
                      {isSavingBrandingDefaults ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Wird gespeichert...</>
                      ) : (
                        'Speichern'
                      )}
                    </Button>
                    {brandingDefaultsSuccess && (
                      <span className="text-emerald-600 text-sm flex items-center gap-1 animate-fade-in">
                        <CheckCircle className="w-4 h-4" /> Gespeichert
                      </span>
                    )}
                  </div>
                )}

                {isTeamMember && (
                  <p className="text-sm text-muted-foreground">
                    Vom Team-Administrator festgelegt.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Info Box */}
            <Alert className="bg-muted/50 border-muted">
              <Sparkles className="h-4 w-4" />
              <AlertTitle>Tipp</AlertTitle>
              <AlertDescription>
                Gespeicherte Assets kannst du im Branding-Schritt deines Workflows schnell auswählen,
                ohne sie erneut hochladen zu müssen. Overlays sollten als PNG mit transparentem
                Hintergrund im 1:1 Format vorliegen.
              </AlertDescription>
            </Alert>
            </AccordionContent>
          </AccordionItem>
          </Accordion>
        </TabsContent>

        {/* Team Tab - Different content for admins vs team members */}
        <TabsContent value="team" className="space-y-6 max-w-4xl animate-fade-in">
          {isTeamMember ? (
            // ======== TEAM MEMBER VIEW ========
            <>
              {/* Team Membership Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    Mein Team
                  </CardTitle>
                  <CardDescription>
                    Du bist Mitglied eines Teams.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingTeam ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : teamAdminInfo ? (
                    <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground">Team Administrator</p>
                        <p className="font-semibold text-foreground">{teamAdminInfo.admin_name || 'Unbekannt'}</p>
                        <p className="text-sm text-muted-foreground">{teamAdminInfo.admin_email}</p>
                      </div>
                      <Badge variant="default" className="bg-emerald-100 text-emerald-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Aktiv
                      </Badge>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Keine Team-Informationen verfügbar.</p>
                  )}
                </CardContent>
              </Card>

              {/* What this means */}
              <Alert className="bg-blue-50 border-blue-200">
                <Users className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-800 font-bold">Was bedeutet das für dich?</AlertTitle>
                <AlertDescription className="text-blue-700 mt-2">
                  <ul className="space-y-1 list-disc pl-4 text-sm">
                    <li>Du hast dein <strong>eigenes Projekt-Dashboard</strong>.</li>
                    <li>Du hast dein <strong>eigenes Credit-Guthaben</strong>. Dein Administrator kann dir Credits zuweisen.</li>
                    <li>Branding-Einstellungen und Assets werden vom Administrator vorgegeben.</li>
                    <li>Du kannst Projekte erstellen und bearbeiten, aber nicht löschen.</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </>
          ) : (
            // ======== ADMIN VIEW ========
            <>
              {/* Team Header */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      Teamverwaltung
                    </span>
                    <Badge variant={teamMembers.length >= 20 ? 'destructive' : 'secondary'}>
                      {teamMembers.length}/20 Plätze belegt
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    Lade bis zu 20 Teammitglieder ein. Jedes Mitglied hat eigene Projekte und ein eigenes Credit-Guthaben.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Invite Form */}
                  <form onSubmit={handleInviteTeamMember} className="space-y-4">
                    <div className="flex gap-3">
                      <div className="flex-1 relative">
                        <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="email"
                          placeholder="mitarbeiter@firma.de"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          className="pl-9"
                          disabled={isInviting || teamMembers.length >= 20}
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={isInviting || teamMembers.length >= 20 || !inviteEmail}
                      >
                        {isInviting ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <UserPlus className="mr-2 h-4 w-4" />
                        )}
                        Einladen
                      </Button>
                    </div>

                    {inviteError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{inviteError}</AlertDescription>
                      </Alert>
                    )}

                    {inviteSuccess && (
                      <Alert className="bg-emerald-50 border-emerald-200 text-emerald-800">
                        <CheckCircle className="h-4 w-4 text-emerald-600" />
                        <AlertDescription>{inviteSuccess}</AlertDescription>
                      </Alert>
                    )}
                  </form>

                  {/* Team Members List */}
                  {isLoadingTeam ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : teamMembers.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">Noch keine Teammitglieder</p>
                      <p className="text-sm mt-1">Lade Mitarbeiter ein, um gemeinsam zu arbeiten.</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">E-Mail</th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Credits</th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Hinzugefügt</th>
                            <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Aktion</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {teamMembers.map((member) => (
                            <tr key={member.id} className="hover:bg-muted/30 transition-colors">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  {member.avatar_path ? (
                                    <img src={getAvatarUrl(member.avatar_path)} alt="" className="w-8 h-8 rounded-full object-cover" />
                                  ) : (
                                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                      <User className="w-4 h-4 text-primary" />
                                    </div>
                                  )}
                                  <div>
                                    <p className="text-sm font-medium text-foreground">{member.email}</p>
                                    {member.member_name && (
                                      <p className="text-xs text-muted-foreground">{member.member_name}</p>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                {member.status === 'active' ? (
                                  <Badge variant="default" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Aktiv
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                                    <Clock className="w-3 h-3 mr-1" />
                                    Ausstehend
                                  </Badge>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-foreground">
                                {member.member_credits != null ? member.member_credits : '–'}
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground">
                                {new Date(member.created_at).toLocaleDateString('de-DE', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                })}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveTeamMember(member.id)}
                                  disabled={removingMemberId === member.id}
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  {removingMemberId === member.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <UserMinus className="w-4 h-4" />
                                  )}
                                  <span className="ml-2 hidden sm:inline">Entfernen</span>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Credit Management */}
              {teamMembers.filter((m) => m.status === 'active' && m.member_id).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Coins className="w-5 h-5 text-amber-500" />
                      Credits verwalten
                    </CardTitle>
                    <CardDescription>
                      Dein Guthaben: <strong>{formatCredits(credits)} Credits</strong>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {teamMembers
                      .filter((m) => m.status === 'active' && m.member_id)
                      .map((member) => (
                        <div key={member.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {member.member_name || member.email}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatCredits(member.member_credits ?? 0)} Credits
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0.5"
                              step="0.5"
                              placeholder="Anzahl"
                              value={transferAmounts[member.member_id!] || ''}
                              onChange={(e) =>
                                setTransferAmounts((prev) => ({
                                  ...prev,
                                  [member.member_id!]: e.target.value,
                                }))
                              }
                              className="w-24 h-9"
                              disabled={isTransferring === member.member_id}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                handleWithdrawCredits(
                                  member.member_id!,
                                  parseFloat(transferAmounts[member.member_id!] || '0')
                                )
                              }
                              disabled={
                                isTransferring === member.member_id ||
                                !transferAmounts[member.member_id!] ||
                                parseFloat(transferAmounts[member.member_id!] || '0') <= 0
                              }
                              title="Credits abziehen"
                            >
                              {isTransferring === member.member_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <ArrowDownToLine className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() =>
                                handleTransferCredits(
                                  member.member_id!,
                                  parseFloat(transferAmounts[member.member_id!] || '0')
                                )
                              }
                              disabled={
                                isTransferring === member.member_id ||
                                !transferAmounts[member.member_id!] ||
                                parseFloat(transferAmounts[member.member_id!] || '0') <= 0
                              }
                              title="Credits senden"
                            >
                              {isTransferring === member.member_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                  </CardContent>
                </Card>
              )}

              {/* Info Box */}
              <Alert className="bg-primary/5 border-primary/20">
                <Users className="h-4 w-4 text-primary" />
                <AlertTitle className="text-primary font-bold">So funktioniert das Team</AlertTitle>
                <AlertDescription className="text-primary/90 mt-2">
                  <ul className="space-y-1 list-disc pl-4 text-sm">
                    <li>Du kannst <strong>alle Projekte</strong> deiner Teammitglieder einsehen.</li>
                    <li>Jedes Mitglied hat ein <strong>eigenes Credit-Guthaben</strong>. Du kannst Credits überweisen und wieder abziehen.</li>
                    <li><strong>Ausstehend</strong> bedeutet: Die Person muss sich erst mit dieser E-Mail registrieren.</li>
                    <li>Nach der Registrierung wird das Mitglied automatisch aktiviert.</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </>
          )}
        </TabsContent>

        {/* Affiliate/Partner Tab */}
        <TabsContent value="affiliate" className="space-y-6 max-w-4xl animate-fade-in">
          {/* Bank Details Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Auszahlungsinformationen
              </CardTitle>
              <CardDescription>
                Hinterlege deine Bankdaten für die Auszahlung deiner Affiliate-Provisionen. 
                Auszahlungen erfolgen ab einem Guthaben von 50€.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {affiliateError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Fehler</AlertTitle>
                  <AlertDescription>{affiliateError}</AlertDescription>
                </Alert>
              )}

              {/* Account Holder */}
              <div className="space-y-2">
                <Label htmlFor="account-holder">
                  Kontoinhaber <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="account-holder"
                    type="text"
                    value={affiliateAccountHolder}
                    onChange={(e) => setAffiliateAccountHolder(e.target.value)}
                    placeholder="Max Mustermann"
                    className="pl-9"
                  />
                </div>
              </div>

              {/* IBAN */}
              <div className="space-y-2">
                <Label htmlFor="iban">
                  IBAN <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="iban"
                    type="text"
                    value={affiliateIban}
                    onChange={(e) => setAffiliateIban(e.target.value)}
                    placeholder="DE89 3704 0044 0532 0130 00"
                    className="pl-9 font-mono"
                  />
                </div>
              </div>

              {/* BIC */}
              <div className="space-y-2">
                <Label htmlFor="bic">BIC (optional)</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="bic"
                    type="text"
                    value={affiliateBic}
                    onChange={(e) => setAffiliateBic(e.target.value)}
                    placeholder="COBADEFFXXX"
                    className="pl-9 font-mono uppercase"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Für SEPA-Überweisungen innerhalb der EU optional
                </p>
              </div>

              {/* Tax ID */}
              <div className="space-y-2">
                <Label htmlFor="tax-id">USt-IdNr. (optional)</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="tax-id"
                    type="text"
                    value={affiliateTaxId}
                    onChange={(e) => setAffiliateTaxId(e.target.value)}
                    placeholder="DE123456789"
                    className="pl-9 font-mono uppercase"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Falls du als Unternehmer am Partnerprogramm teilnimmst
                </p>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button
                  onClick={handleSaveAffiliateSettings}
                  disabled={isSavingAffiliate}
                >
                  {isSavingAffiliate && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
                {affiliateSuccess && (
                  <span className="text-emerald-600 text-sm flex items-center gap-1 animate-fade-in">
                    <CheckCircle className="w-4 h-4" /> Gespeichert
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Info Box */}
          <Alert className="bg-primary/5 border-primary/20">
            <Gift className="h-4 w-4 text-primary" />
            <AlertTitle className="text-primary font-bold">Über das Partnerprogramm</AlertTitle>
            <AlertDescription className="text-primary/90 mt-2">
              <ul className="space-y-1 list-disc pl-4 text-sm">
                <li>Du erhältst <strong>20% Lifetime-Provision</strong> auf alle Käufe deiner geworbenen Nutzer.</li>
                <li>Auszahlungen erfolgen ab einem Guthaben von <strong>50€</strong>.</li>
                <li>Die Auszahlung erfolgt per SEPA-Überweisung auf das hier hinterlegte Konto.</li>
                <li>Du erhältst eine Benachrichtigung per E-Mail, sobald eine Auszahlung erfolgt ist.</li>
              </ul>
            </AlertDescription>
          </Alert>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============================================================================
// Asset Card Component
// ============================================================================

interface AssetCardProps {
  asset: BrandingAsset;
  onDelete: (asset: BrandingAsset) => void;
  isOverlay?: boolean;
  isWatermark?: boolean;
  readOnly?: boolean;
}

const AssetCard: React.FC<AssetCardProps> = ({ asset, onDelete, isOverlay, isWatermark, readOnly }) => {
  return (
    <Card className="group relative overflow-hidden aspect-square border-border">
      <img
        src={asset.publicUrl}
        alt={asset.name}
        className={`w-full h-full ${isOverlay || isWatermark ? 'object-contain p-2' : 'object-contain p-3'}`}
        style={(isOverlay || isWatermark) ? { backgroundColor: '#e2e8f0' } : undefined}
      />
      
      {/* Only show delete button if not readOnly */}
      {!readOnly && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center">
          <button
            onClick={() => onDelete(asset)}
            className="p-2 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/90"
            title="Löschen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
        <p className="text-white text-xs truncate">{asset.name}</p>
      </div>
    </Card>
  );
};
